import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, MapPin, Mail, Phone, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, NAV_LINKS, SOCIAL } from "../data";
import { Logo } from "./Logo";
import { api } from "../lib/api";

export function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;

  async function subscribe(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("Subscribed. Expect our best insights.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Subscription failed";
      toast.error(typeof msg === "string" ? msg : "Subscription failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <footer data-testid="site-footer" className="relative bg-[#0F1E3C] text-white overflow-hidden mt-24">
      <div className="blob-amber top-0 right-0 opacity-40" />

      <section className="container-x pt-20 sm:pt-24 pb-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Logo size="md" variant="invert" />
            <p className="mt-6 text-base text-white/70 leading-relaxed max-w-md">
              {COMPANY.positioning}. A consulting and recruitment firm helping US
              organizations build the teams that move their business forward.
            </p>
            <p className="mt-3 text-xs text-white/50 leading-relaxed max-w-md" data-testid="footer-dba">
              {COMPANY.dbaStatement}.
            </p>
            <div className="mt-8 space-y-3 text-sm text-white/70">
              <div data-testid="footer-address" className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-[#E9610E] shrink-0" />
                <span>{COMPANY.address}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#E9610E] shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-white transition-colors">
                  {COMPANY.email}
                </a>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <SocialLink href={SOCIAL.linkedin} icon={<Linkedin className="w-4 h-4" />} label="LinkedIn" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-5 font-semibold">Company</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/privacy" data-testid="footer-nav-privacy" className="text-sm text-white/70 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/sms-terms" data-testid="footer-nav-sms-terms" className="text-sm text-white/70 hover:text-white transition-colors">
                  SMS Terms &amp; Conditions
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-5 font-semibold">Stay In The Loop</p>
            <p className="text-sm text-white/70 mb-5 leading-relaxed">
              Quarterly insights on hiring, compensation benchmarks, and market shifts.
            </p>
            <form onSubmit={subscribe} className="flex gap-2">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-3 text-sm text-white placeholder-white/50 focus:border-[#E9610E] focus:outline-none"
              />
              <button
                data-testid="newsletter-submit-btn"
                type="submit"
                disabled={loading}
                className="px-5 py-3 rounded-full bg-[#E9610E] text-white text-sm font-semibold disabled:opacity-50 hover:bg-[#D55100] transition-colors"
              >
                {loading ? "..." : "Join"}
              </button>
            </form>

            <div className="mt-8 p-5 rounded-2xl border border-white/10 bg-white/5">
              <p className="text-xs uppercase tracking-[0.15em] text-[#E9610E] mb-2 font-semibold">Need Help?</p>
              <p className="text-sm text-white/80">
                Email <a href={`mailto:${COMPANY.email}`} className="underline hover:text-white">{COMPANY.email}</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-white/50 text-center sm:text-left">
          &copy; {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.
          <span className="hidden sm:inline"> · </span>
          <br className="sm:hidden" />
          <span className="text-white/60">{COMPANY.dbaStatement}.</span>
        </p>
        <div className="flex items-center gap-6 text-xs text-white/50 flex-wrap justify-center">
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy Policy</Link>
          <Link to="/sms-terms" data-testid="footer-sms-terms-link" className="hover:text-white">SMS Terms</Link>
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white">Admin</Link>
        </div>
      </section>
    </footer>
  );
}

function SocialLink({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={`social-${label.toLowerCase()}`}
      className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-[#E9610E] hover:bg-[#E9610E]/10 transition-all"
    >
      {icon}
    </a>
  );
}
